import React, { useMemo } from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';

interface LoadingModalProps {
  visible: boolean;
  message?: string;
}

// RN-ALIGNED: Clean createStyles function matching React Native pattern  
const createStyles = (theme: Theme) => ({
  loadingModalContainer: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.modalOverlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  loadingModal: {
    padding: `${theme.spacing.xxxl}px ${theme.spacing.xxxl * 1.5}px`,
    borderRadius: theme.radii.xl,
    backgroundColor: theme.colors.modalBackground,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 280,
    boxShadow: theme.shadows.modal,
  },
  loadingText: {
    marginTop: theme.spacing.xl,
    fontSize: theme.typography.sizes.lg,
    fontWeight: theme.typography.weights.medium,
    color: theme.colors.text,
    textAlign: 'center' as const,
    maxWidth: 240,
  },
  // WEB-SPECIFIC: Custom spinner (replaces RN ActivityIndicator) - matching "large" size
  spinner: {
    width: 36, // Increased to match RN ActivityIndicator "large" size
    height: 36, // Increased to match RN ActivityIndicator "large" size
    border: `3px solid ${theme.colors.border}`,
    borderTop: `3px solid ${theme.colors.secondary}`,
    borderRadius: theme.radii.full,
    animation: 'loading-spin 1s linear infinite',
  },
});

const LoadingModal: React.FC<LoadingModalProps> = ({
  visible,
  message = 'Processing...'
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  // WEB-SPECIFIC: Add CSS keyframes for spinner (RN uses ActivityIndicator)
  React.useEffect(() => {
    // Only add if not already added
    if (!document.querySelector('#loading-spinner-keyframes')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'loading-spinner-keyframes';
      styleElement.textContent = `
        @keyframes loading-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(styleElement);
    }
  }, []);

  // WEB-SPECIFIC: Return null instead of using Modal component visibility
  // React Native Modal has built-in visible prop, web handles this with conditional rendering
  if (!visible) {
    return null;
  }

  return (
    <div style={styles.loadingModalContainer}>
      <div style={styles.loadingModal}>
        {/* WEB-SPECIFIC: Custom spinner div (RN uses ActivityIndicator) */}
        <div style={styles.spinner} />
        <div style={styles.loadingText}>
          {message}
        </div>
      </div>
    </div>
  );
};

export default LoadingModal;
